/**
 * 지점 허브 분기 조건 — spec §1.
 *
 * 선택된 카테고리가 비어 있지 않고 **전부** 모빌리티(오토바이·스쿠터) 코드거나
 * **전부** 캠핑 중분류(10-04, 하위 포함)면
 * 검색 페이지는 상품 카드 목록 대신 지점 허브를 렌더한다. 칩을 지워 선택이 비면
 * 곧바로 기존 상품 모드로 돌아온다.
 *
 * (데이터 레이어 에이전트가 `bike-classes.ts` 에 같은 상수를 넣으면 그쪽을 정본으로
 *  삼고 이 파일은 재노출만 남기면 된다.)
 */

import { expandCategoryCodes } from "@/config/categories";

/** 오토바이·스쿠터 — 09-28 한 카테고리로 합쳤다(옛 오토바이 50-01-05 → 이 코드). */
export const MOBILITY_CATEGORY_CODE = "50-01-02";
/** 옛 오토바이 코드 — 비활성. 정규화 전 입력(옛 링크)도 허브로 받기 위해 남긴다. */
export const LEGACY_MOTORCYCLE_CATEGORY_CODE = "50-01-05";

export const STATION_HUB_CATEGORY_CODES = [
  MOBILITY_CATEGORY_CODE,
  LEGACY_MOTORCYCLE_CATEGORY_CODE,
] as const;

/** 캠핑 중분류(10-04-01 백패킹 · 10-04-02 텐트 · 10-04-03 차박). 핀 아이콘·홈 상한의 기준. */
export const CAMPING_CATEGORY_CODE = "10-04";

/**
 * 캠핑장 허브를 여는 코드 — 텐트 세트만(10-04-02). 캠핑장에서 받는 상품이라 지도 화면이 맞다.
 * 캠핑 전체(10-04)·백패킹(전국 택배 등산 세트)·차박(상품 없음)은 일반 상품 목록으로 간다 —
 * 캠핑장 화면에 묻히면 후지산 3곳에만 연결된 백패킹 세트가 보이지 않았다.
 */
export const CAMPING_HUB_CATEGORY_CODES: readonly string[] = ["10-04-02"];

/**
 * 캠핑 허브 화면([장비 | 캠핑장])을 여는 **선택** — 캠핑 중분류 전체(10-04 · 백패킹 · 텐트 · 차박,
 * 10-15). 장비 탭이 기본이고 택배 장비도 남기므로 캠핑장에 묶이지 않은 세트도 보인다.
 * 캠핑장 페이지·랜딩의 범위(위 CAMPING_HUB_CATEGORY_CODES, 텐트 세트)와는 별개다.
 */
const CAMPING_HUB_SELECTION_CODES: ReadonlySet<string> = new Set(
  expandCategoryCodes([CAMPING_CATEGORY_CODE]),
);

/**
 * 허브 종류 — 같은 지점 허브 화면을 카테고리에 따라 두 벌로 쓴다.
 *  - `mobility`: 오토바이·스쿠터 [지점 | 차종], 배기량·타입 등 필터 축
 *  - `camping`:  캠핑 [캠핑장 | 장비], 인원 축(10-05)만. 장비 탭은 일반 검색 결과(택배 포함)
 */
export type HubKind = "mobility" | "camping";

function isCampingCode(code: string): boolean {
  return CAMPING_HUB_SELECTION_CODES.has(code);
}

/**
 * 선택된 카테고리가 허브를 여는가 — 비어 있지 않고 **전부** 한 종류의 코드일 때만.
 * 모빌리티와 캠핑이 섞이면 허브가 아니다(상품 모드).
 */
export function hubKindOf(selectedCategories: readonly string[]): HubKind | null {
  if (selectedCategories.length === 0) return null;
  const mobility: readonly string[] = STATION_HUB_CATEGORY_CODES;
  if (selectedCategories.every((code) => mobility.includes(code))) {
    return "mobility";
  }
  if (selectedCategories.every(isCampingCode)) return "camping";
  return null;
}

/**
 * 지점 핀의 카테고리 코드 — 지도 마커가 이 값으로 아이콘을 고른다. 한 허브 안에서는
 * 지점마다 같다. 마커는 코드가 비면 아이콘이 빈 채로 그려지므로 항상 돌려준다.
 */
export function stationCategoryCode(kind: HubKind = "mobility"): string {
  return kind === "camping" ? CAMPING_CATEGORY_CODE : MOBILITY_CATEGORY_CODE;
}

export function isStationHubSelection(selectedCategories: string[]): boolean {
  return hubKindOf(selectedCategories) !== null;
}

/**
 * 허브 데이터셋이 조회할 카테고리 코드(정렬·중복 제거 — 캐시 키로 쓴다).
 * 모빌리티는 오토바이·스쿠터를 **한 묶음**으로 본다(어느 쪽을 골라도 캐시 엔트리 하나).
 * 캠핑은 고른 코드 그대로 — 하위 코드 전개는 조회부(`expandCategoryCodes`)가 한다.
 * 허브 선택이 아니면 입력을 그대로 정리해 돌려준다.
 */
export function hubDatasetCodes(selectedCategories: readonly string[]): string[] {
  const codes =
    hubKindOf(selectedCategories) === "mobility"
      ? [...STATION_HUB_CATEGORY_CODES]
      : [...selectedCategories];
  return [...new Set(codes.filter(Boolean))].sort();
}

/** 캠핑 허브가 자기 문구(`hub.camping.*`)로 바꿔 쓰는 키. */
const CAMPING_KEYS = new Set([
  "modeStations",
  "modeCatalog",
  "stationsN",
  "catalogHead",
  "bikesN",
  "noResult",
  "emptyFilterTitle",
  "emptyFilterBody",
  "emptyAreaTitle",
  "emptyAreaBody",
  "emptyAreaAction",
]);

/**
 * 종류별 허브 문구 키 — 캠핑이면 `hub.camping.<key>`, 아니면 `hub.<key>`.
 * 모빌리티 문구(지점·차종)는 그대로 둔다.
 */
export function hubKey(kind: HubKind, key: string): string {
  return kind === "camping" && CAMPING_KEYS.has(key)
    ? `hub.camping.${key}`
    : `hub.${key}`;
}

/**
 * 지점 목록이 비었을 때 **왜** 비었는가(09-29) — 빈 화면의 문구·버튼을 고른다.
 *  - `filters`: 필터 축(바이크: 제조사·배기량·타입·라이딩·변속 / 캠핑: 인원) 때문 → "필터 초기화"
 *  - `area`:    "이 지역에서 검색" 범위(MAP_AREA) 때문 → "전체 지점 보기"(범위 해제)
 *  - `none`:    둘 다 아님(데이터 자체가 없음) → 안내만, 버튼 없음
 *
 * 둘 다 걸려 있으면 필터가 먼저다 — 단, 범위 안에 지점이 **하나도 없으면** 필터를 풀어도
 * 여전히 비므로 그때는 범위를 원인으로 본다. `unfilteredCount` 는 범위만 적용하고 필터
 * 축은 적용하지 않은 지점 수다.
 */
export type HubEmptyCause = "filters" | "area" | "none";

export function hubEmptyCause(input: {
  filtersActive: boolean;
  areaBounded: boolean;
  unfilteredCount: number;
}): HubEmptyCause {
  const { filtersActive, areaBounded, unfilteredCount } = input;
  if (filtersActive && (unfilteredCount > 0 || !areaBounded)) return "filters";
  if (areaBounded) return "area";
  return "none";
}

/** 지점 상세 액션 한 번에 보낼 지점 id 상한 — "use server" 파일은 async 함수만 export 가능하므로 여기 둔다. */
export const STATION_DETAILS_CHUNK = 200;
